import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  Icon,
  Spinner,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FiTrendingUp, FiActivity, FiAward } from "react-icons/fi";
// TODO: This icon isn't great change it
import { FaRocket } from "react-icons/fa";
import { useGetAgendaReportQuery } from "@store/api/report";
import { useGetPastAgendasForReportQuery } from "@store/api/agenda";
import { $Routes } from "@routes";
// import { BottomNavigation } from "@components";
import { AgendaListItem } from "@components/Agenda";
import { useSdkUtils, useToastNotification, useProfile } from "@hooks";
import EReportLimitModal from "@components/Modals/EReportLimitModal";
// import { LICENSE_TYPES } from "@constants";
import { useCheckEReportAccessMutation } from "@store/api/report";
import { isZoomApp } from "@utils/helpers";
import { DemoActionCard } from "@components/Demo";
import { DEMO_STEPS } from "@constants/demoFlow";
import { endDemo } from "@store/demoSlice";
import { trackDemoEvent } from "@utils/demoAnalytics";
import { hasDemoGuestToken, clearDemoGuestToken } from "@utils/guestDemo";

/**
 * ReportHeader component for consistent header styling across report states
 */
const ReportHeader = ({ subtitle, subtitleColor = "#8E8E93" }) => (
  <Box
    px="17.5px"
    py="12px"
    bg="#FAFAFB"
    borderBottom="1px solid rgba(208, 204, 213, 0.30)"
  >
    <Text
      fontSize="18px"
      fontWeight="700"
      lineHeight="1.33"
      color="#433358"
      fontFamily="Geist, sans-serif"
    >
      Engagement Report
    </Text>
  </Box>
);

/**
 * ReportDetailView displays detailed engagement report for a specific agenda
 * Matches the Figma design exactly with E-Score, Activities, Boost metrics
 * and sections for top activities, recommendations, and top performers
 */
const ReportDetailView = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { agendaId } = useParams();
  const [searchParams] = useSearchParams();
  const { showErrorToast } = useToastNotification();
  const { openUrl } = useSdkUtils();
  useProfile();
  const [isEReportLimitModalOpen, setIsEReportLimitModalOpen] =
    React.useState(false);
  const [checkAccess] = useCheckEReportAccessMutation();

  // Demo mode state
  const demoIsActive = useSelector((state) => state.demo.isActive);
  const demoCurrentStep = useSelector((state) => state.demo.currentStep);
  const isDemoReport = searchParams.get("demo") === "true";
  const isGuestDemo = hasDemoGuestToken();
  const [showDemoCta, setShowDemoCta] = useState(false);

  // Show REPORT_CTA modal with 1.5s delay when demo is active on report page
  useEffect(() => {
    if (demoIsActive && isDemoReport && demoCurrentStep === "REPORT_CTA") {
      const timer = setTimeout(() => {
        setShowDemoCta(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [demoIsActive, isDemoReport, demoCurrentStep]);

  // Handle demo CTA actions
  const handleDemoReportAction = useCallback(
    async (actionId) => {
      if (actionId === "VIEW_FULL_REPORT") {
        trackDemoEvent("demo_report_viewed", { agendaId });
        // Open external report
        try {
          const url = `${process.env.REACT_APP_EREPORT_URL}/?agenda=${agendaId}`;
          if (isZoomApp) {
            await openUrl(url);
          } else {
            window.open(url, "_blank");
          }
        } catch (error) {
          // Still end the demo even if report open fails
        }
        trackDemoEvent("demo_completed", { agendaId });
        dispatch(endDemo());
        setShowDemoCta(false);
      } else if (actionId === "CREATE_SMART_AGENDA") {
        trackDemoEvent("demo_smart_agenda_clicked", { fromStep: "REPORT_CTA" });
        dispatch(endDemo());
        setShowDemoCta(false);
        // A guest converts by signing up: on signup their built smart agenda
        // (if any) is claimed (P1-F1) and the demo agenda is left to TTL-expire.
        if (isGuestDemo) {
          clearDemoGuestToken();
          navigate($Routes.registerPage);
        } else {
          navigate($Routes.smartAgendasPage);
        }
      }
    },
    [agendaId, dispatch, navigate, openUrl, isGuestDemo],
  );

  const handleEndDemoFromReport = useCallback(() => {
    dispatch(endDemo());
    setShowDemoCta(false);
  }, [dispatch]);

  // Fetch the specific agenda details. Guests can't hit the (auth-gated) past
  // agendas list and don't need it — the demo report is driven by the report
  // query (guest-token authorized) and the demo CTA.
  const { data: pastAgendasResponse, isLoading: isLoadingAgendas } =
    useGetPastAgendasForReportQuery(undefined, { skip: isGuestDemo });

  // Find the specific agenda
  const agenda = pastAgendasResponse?.pastAgendas?.find(
    (a) => a._id === agendaId,
  );

  // Fetch report data for this specific agenda
  const {
    data: reportData,
    isLoading: isLoadingReport,
    error: reportError,
  } = useGetAgendaReportQuery({ agendaId }, { skip: !agendaId });

  const isLoading = isLoadingAgendas || isLoadingReport;

  const handleBackClick = () => {
    navigate($Routes.reportsPage);
  };

  const openExternalReport = async () => {
    try {
      await checkAccess({ agendaId }).unwrap();

      const url = isZoomApp
        ? `${process.env.REACT_APP_EREPORT_URL}/?agenda=${agendaId}`
        : `${process.env.REACT_APP_EREPORT_URL}/?agenda=${agendaId}`;

      if (isZoomApp) {
        await openUrl(url);
      } else {
        window.open(url, "_blank");
      }
    } catch (error) {
      if (
        error?.status === 403 ||
        error?.data?.message?.includes("SUBSCRIPTION_ITEM_USAGE_LIMIT") ||
        error?.dataErrorType === "FEATURE_LIMIT"
      ) {
        setIsEReportLimitModalOpen(true);
        return;
      }
      showErrorToast("Failed to open e-report");
    }
  };

  // Error state - differentiate between "no report" and other errors
  if (reportError) {
    const isNoReportError =
      reportError?.status === 404 ||
      reportError?.data?.message?.toLowerCase().includes("not found") ||
      reportError?.data?.message?.toLowerCase().includes("no report");

    if (isNoReportError) {
      return (
        <Box bg="#FAFAFB" h="100vh" display="flex" flexDirection="column">
          {/* Fixed Header */}
          <ReportHeader />

          {/* Scrollable Content */}
          <Box flex="1" overflowY="auto" px="17.5px" py="28px">
            <VStack spacing="24px" align="stretch">
              {/* No Report Available Message */}
              <Alert
                status="info"
                bg="blue.50"
                borderRadius="12px"
                p="24px"
                borderColor="blue.200"
                borderWidth="1px"
              >
                <AlertIcon color="blue.500" />
                <Box flex="1">
                  <AlertTitle
                    fontSize="16px"
                    fontWeight="600"
                    color="#433358"
                    fontFamily="Geist, sans-serif"
                    mb="8px"
                  >
                    No Report Available
                  </AlertTitle>
                  <AlertDescription
                    color="#433358"
                    fontFamily="Geist, sans-serif"
                    fontSize="14px"
                    lineHeight="1.43"
                  >
                    This agenda doesn't have an engagement report yet. Reports
                    are generated automatically after meetings are completed
                    with participant activity.
                  </AlertDescription>
                </Box>
              </Alert>

              {/* Action Button */}
              <Button
                bg="#4C47CC"
                color="white"
                fontFamily="Geist, sans-serif"
                fontWeight="700"
                fontSize="13px"
                h="auto"
                py={4}
                px="55px"
                borderRadius="8px"
                onClick={handleBackClick}
                _hover={{
                  bg: "#3D37B8",
                }}
                _active={{
                  bg: "#332A9F",
                }}
              >
                Back to Reports
              </Button>
            </VStack>
          </Box>
        </Box>
      );
    }

    // General error state for other types of errors
    return (
      <Box bg="#FAFAFB" h="100vh" display="flex" flexDirection="column">
        {/* Fixed Header */}
        <ReportHeader subtitle="Error Loading Report" subtitleColor="#433358" />

        {/* Scrollable Content */}
        <Box flex="1" overflowY="auto" px="17.5px" py="28px">
          <VStack spacing="24px" align="stretch">
            <Alert
              status="error"
              bg="red.50"
              borderRadius="12px"
              p="24px"
              borderColor="red.200"
              borderWidth="1px"
            >
              <AlertIcon color="red.500" />
              <Box flex="1">
                <AlertTitle
                  fontSize="16px"
                  fontWeight="600"
                  color="#433358"
                  fontFamily="Geist, sans-serif"
                  mb="8px"
                >
                  Unable to Load Report
                </AlertTitle>
                <AlertDescription
                  color="#433358"
                  fontFamily="Geist, sans-serif"
                  fontSize="14px"
                  lineHeight="1.43"
                >
                  There was an error loading the report data. Please try again
                  later or contact support if the issue persists.
                </AlertDescription>
              </Box>
            </Alert>

            <Button
              bg="#4C47CC"
              color="white"
              fontFamily="Geist, sans-serif"
              fontWeight="700"
              fontSize="13px"
              h="auto"
              py={4}
              px="55px"
              borderRadius="8px"
              onClick={handleBackClick}
              _hover={{
                bg: "#3D37B8",
              }}
              _active={{
                bg: "#332A9F",
              }}
            >
              Back to Reports
            </Button>
          </VStack>
        </Box>
      </Box>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <Box bg="#FAFAFB" h="100vh" display="flex" flexDirection="column">
        {/* Fixed Header */}
        <ReportHeader subtitle="Loading..." subtitleColor="#433358" />

        {/* Scrollable Content */}
        <Box flex="1" overflowY="auto" px="17.5px" py="28px">
          <Flex justify="center" align="center" py="40px">
            <Spinner size="lg" color="#4C47CC" />
            <Text
              ml="12px"
              color="#433358"
              fontSize="16px"
              fontFamily="Geist, sans-serif"
            >
              Loading report...
            </Text>
          </Flex>
        </Box>
      </Box>
    );
  }

  // Extract data with fallbacks
  const eScore = reportData?.eScore;
  const averageAttendance = reportData?.averageAttendance;
  const boost = reportData?.boost;
  const leaderboard = reportData?.leaderboard || [];

  // `agenda` is undefined for a guest demo (the past-agendas list is auth-gated),
  // so fall back to the report payload for the header fields.
  const reportAgendaHeader = {
    _id: agenda?._id || agendaId,
    title: agenda?.title || reportData?.title,
    impactScore: agenda?.impactScore ?? reportData?.impactScore,
    eScore: agenda?.eScore ?? reportData?.eScore,
    meetingType: agenda?.meetingType,
    blueprint: agenda?.blueprint,
  };
  const agendaTitle = reportAgendaHeader.title || "Meeting Report";

  // Guests see "Try for Free!" instead of "Create a Smart Agenda" (same action).
  const reportCtaStepConfig = isGuestDemo
    ? {
        ...DEMO_STEPS.REPORT_CTA,
        buttons: DEMO_STEPS.REPORT_CTA.buttons.map((btn) =>
          btn.action === "CREATE_SMART_AGENDA"
            ? { ...btn, label: "Try for Free!" }
            : btn,
        ),
      }
    : DEMO_STEPS.REPORT_CTA;

  return (
    <Box bg="#FAFAFB" h="100vh" display="flex" flexDirection="column">
      {/* Demo REPORT_CTA Action Card */}
      <DemoActionCard
        isOpen={showDemoCta}
        stepConfig={reportCtaStepConfig}
        onAction={handleDemoReportAction}
        onEndDemo={handleEndDemoFromReport}
      />

      {/* Fixed Header */}
      <ReportHeader subtitle={agendaTitle} subtitleColor="#433358" />

      <AgendaListItem
        id={reportAgendaHeader._id}
        title={reportAgendaHeader.title}
        hideMenu={true}
        isPastAgenda={true}
        impactScore={reportAgendaHeader.impactScore}
        eScore={reportAgendaHeader.eScore}
        meetingType={reportAgendaHeader.meetingType}
        blueprint={reportAgendaHeader.blueprint}
        onMenuToggle={() => {}}
      />

      {/* Scrollable Content */}
      <Box flex="1" overflowY="auto" px="17.5px" py="28px" pb="60px">
        {/* E-Report Limit Modal */}
        <EReportLimitModal
          isOpen={isEReportLimitModalOpen}
          onClose={() => setIsEReportLimitModalOpen(false)}
        />
        <VStack spacing="27px" align="stretch">
          {/* Metrics Cards Row */}
          <HStack spacing="12px" align="stretch" overflow={"auto"}>
            {/* E-Score Card */}
            <Box bg="#F6F5F7" borderRadius="12px" p="12px" w="122px" h="155px">
              <VStack align="start" spacing="14px" h="100%">
                <HStack spacing="7px">
                  <Box
                    bg="#D1FADF"
                    borderRadius="7px"
                    p="5px"
                    w="28px"
                    h="28px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={FiTrendingUp} w="18px" h="18px" color="#12B76A" />
                  </Box>
                  <Text
                    fontSize="13px"
                    fontWeight="700"
                    lineHeight="1.23"
                    color="#433358"
                    fontFamily="Geist, sans-serif"
                  >
                    E-Score
                  </Text>
                </HStack>

                <Text
                  fontSize="20px"
                  fontWeight="700"
                  lineHeight="1.4"
                  letterSpacing="-3%"
                  color="#433358"
                  fontFamily="Geist, sans-serif"
                >
                  {eScore}%
                </Text>

                <Text
                  fontSize="12px"
                  fontWeight="400"
                  lineHeight="1.5"
                  letterSpacing="-1%"
                  color="#433358"
                  fontFamily="Geist, sans-serif"
                  w="97.84px"
                >
                  Medium or Strong engagement
                </Text>
              </VStack>
            </Box>

            {/* Activities Card */}
            <Box bg="#F6F5F7" borderRadius="12px" p="12px" w="122px" h="155px">
              <VStack align="start" spacing="14px" h="100%">
                <HStack spacing="7px">
                  <Box
                    bg="#ECE5FD"
                    borderRadius="7px"
                    p="5px"
                    w="28px"
                    h="28px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={FiActivity} w="18px" h="18px" color="#6533EE" />
                  </Box>
                  <Text
                    fontSize="13px"
                    fontWeight="700"
                    lineHeight="1.23"
                    color="#433358"
                    fontFamily="Geist, sans-serif"
                  >
                    Activities
                  </Text>
                </HStack>

                <Text
                  fontSize="20px"
                  fontWeight="700"
                  lineHeight="1.4"
                  letterSpacing="-3%"
                  color="#433358"
                  fontFamily="Geist, sans-serif"
                >
                  {averageAttendance}%
                </Text>

                <Text
                  fontSize="12px"
                  fontWeight="84"
                  lineHeight="1.5"
                  letterSpacing="-1%"
                  color="#433358"
                  fontFamily="Geist, sans-serif"
                  w="88.4px"
                >
                  Participated in at least one activity
                </Text>
              </VStack>
            </Box>

            {/* Boost Card */}
            <Box bg="#F6F5F7" borderRadius="12px" p="12px" w="122px" h="155px">
              <VStack align="start" spacing="14px" h="100%">
                <HStack spacing="7px">
                  <Box
                    bg="#FEF0C7"
                    borderRadius="7px"
                    p="5px"
                    w="28px"
                    h="28px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={FaRocket} w="18px" h="18px" color="#B54708" />
                  </Box>
                  <Text
                    fontSize="13px"
                    fontWeight="700"
                    lineHeight="1.23"
                    color="#433358"
                    fontFamily="Geist, sans-serif"
                  >
                    Boost
                  </Text>
                </HStack>

                <Text
                  fontSize="20px"
                  fontWeight="700"
                  lineHeight="1.4"
                  letterSpacing="-3%"
                  color="#433358"
                  fontFamily="Geist, sans-serif"
                >
                  {boost}%
                </Text>

                <Text
                  fontSize="12px"
                  fontWeight="400"
                  lineHeight="1.5"
                  letterSpacing="-1%"
                  color="#433358"
                  fontFamily="Geist, sans-serif"
                  w="102px"
                >
                  Max engagement spike from activities
                </Text>
              </VStack>
            </Box>
          </HStack>

          {/* Content Sections */}
          {/* Top Activity Section */}
          <VStack spacing="13px" align="stretch">
            <HStack spacing="8px">
              <Box
                bg="#FEF0C7"
                borderRadius="7px"
                p="5px"
                w="28px"
                h="28px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={FaRocket} w="18px" h="18px" color="#B54708" />
              </Box>
              <Text
                fontSize="18px"
                fontWeight="700"
                lineHeight="1.33"
                color="#433358"
                fontFamily="Geist, sans-serif"
                w="114px"
              >
                Top Activity
              </Text>
            </HStack>

            {/* TODO: Add top activity */}
            <Box bg="#F6F5F7" borderRadius="12px" h="50px" px="16px" py="13px">
              <Text
                fontSize="16px"
                fontWeight="400"
                lineHeight="1.5"
                letterSpacing="-2%"
                color="#433358"
                fontFamily="Geist, sans-serif"
              >
                Icebreaker - {boost}% Boost
              </Text>
            </Box>
          </VStack>

          {/* Recommendations Section */}
          {/* TODO: Implement Insights section */}
          {/* <VStack spacing="13px" align="stretch">
            <Text
              fontSize="18px"
              fontWeight="700"
              lineHeight="1.33"
              color="#433358"
              fontFamily="Geist, sans-serif"
            >
              Insights
            </Text>

            <Text
              fontSize="16px"
              fontWeight="84"
              lineHeight="1.5"
              letterSpacing="-2%"
              color="#433358"
              fontFamily="Geist, sans-serif"
            >
              Consider starting the next agenda with a quick poll to enhance
              early engagement.
            </Text>
          </VStack> */}

          {/* Townhall Top 5 Section */}
          <VStack spacing="13px" align="stretch" marginBottom="27px">
            <HStack spacing="8px">
              <HStack spacing="8px">
                <Box
                  bg="#FEF0C7"
                  borderRadius="4px"
                  p="6px"
                  w="26px"
                  h="26px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiAward} w="14px" h="14px" color="#93370D" />
                </Box>
                <Text
                  fontSize="18px"
                  fontWeight="700"
                  lineHeight="1.33"
                  color="#433358"
                  fontFamily="Geist, sans-serif"
                >
                  Townhall Top 5
                </Text>
              </HStack>
            </HStack>

            {/* Display leaderboard participants */}
            {leaderboard.length > 0 ? (
              <VStack spacing="8px" align="stretch">
                {leaderboard.slice(0, 5).map((participant, index) => (
                  <Box
                    key={participant.userId?._id || index}
                    bg="#F6F5F7"
                    borderRadius="12px"
                    h="50px"
                    px="16px"
                    py="13px"
                  >
                    <HStack justify="space-between" align="center" h="100%">
                      <HStack spacing="8px">
                        <Text
                          fontSize="14px"
                          fontWeight="700"
                          color="#433358"
                          fontFamily="Geist, sans-serif"
                        >
                          #{index + 1}
                        </Text>
                        <Text
                          fontSize="16px"
                          fontWeight="84"
                          lineHeight="1.5"
                          letterSpacing="-2%"
                          color="#433358"
                          fontFamily="Geist, sans-serif"
                        >
                          {participant.userId?.name ||
                            participant.name ||
                            "Participant"}
                        </Text>
                      </HStack>

                      <HStack spacing="12px">
                        {participant.userId?.avatar ? (
                          <Box
                            w="20px"
                            h="20px"
                            borderRadius="100px"
                            overflow="hidden"
                          >
                            <img
                              src={participant.userId.avatar}
                              alt={participant.userId.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        ) : (
                          <Box
                            w="20px"
                            h="20px"
                            bg="white"
                            borderRadius="100px"
                          />
                        )}
                        <Text
                          fontSize="16px"
                          fontWeight="84"
                          lineHeight="1.5"
                          letterSpacing="-2%"
                          color="#433358"
                          fontFamily="Geist, sans-serif"
                        >
                          +{participant.points || 0} points
                        </Text>
                      </HStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Box bg="#F6F5F7" borderRadius="12px" px="16px" py="13px">
                <Text
                  fontSize="14px"
                  fontWeight="400"
                  lineHeight="1.5"
                  color="#8B8DA6"
                  fontFamily="Geist, sans-serif"
                  textAlign="center"
                >
                  No leaderboard data available for this meeting
                </Text>
              </Box>
            )}
          </VStack>

          {/* View Full E-Report Button */}
          <Box bg="#4C47CC" borderRadius="10px" w="100%">
            <Button
              variant="ghost"
              size="md"
              w="100%"
              color="white"
              fontFamily="Geist, sans-serif"
              fontSize="13px"
              fontWeight="700"
              lineHeight="1.23"
              _hover={{ bg: "transparent" }}
              _active={{ bg: "transparent" }}
              onClick={openExternalReport}
            >
              View Full E-Report
            </Button>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
};

export default ReportDetailView;
